import { useState } from 'react'
import { X, RotateCcw, Trash2, Loader2, Pencil, Check } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  usePlanVersions,
  useActivateVersion,
  useDeleteVersion,
  useUpdateVersion,
} from '@/hooks/usePlanDetail'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { formatDate } from '@/utils/formatters'

import type { ApiPlanStatus } from '@/types'
import type { VersionSummary } from '@/api/planDetail'

interface ManageVersionsDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  status: ApiPlanStatus
}

export function ManageVersionsDrawer({
  open,
  onOpenChange,
  planId,
  status,
}: ManageVersionsDrawerProps) {
  const { data: versions, isLoading, isError } = usePlanVersions(planId)
  const activateMutation = useActivateVersion(planId)
  const deleteMutation = useDeleteVersion(planId)
  const updateMutation = useUpdateVersion(planId)
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null)
  const [editAliasInput, setEditAliasInput] = useState('')
  const [editNotesInput, setEditNotesInput] = useState('')

  const handleStartEdit = (version: VersionSummary) => {
    setEditingVersionId(version.id)
    setEditAliasInput(version.alias)
    setEditNotesInput(version.notes ?? '')
  }

  const handleCancelEdit = () => {
    setEditingVersionId(null)
    setEditAliasInput('')
    setEditNotesInput('')
  }

  const handleSaveEdit = (version: VersionSummary) => {
    const trimmedAlias = editAliasInput.trim() || version.alias
    const trimmedNotes = editNotesInput.trim()
    const aliasChanged = trimmedAlias !== version.alias
    const notesChanged = trimmedNotes !== (version.notes ?? '')

    if (!aliasChanged && !notesChanged) {
      handleCancelEdit()
      return
    }

    // Send '' rather than null to clear notes: the API treats an explicit
    // null as "field not provided" and leaves the existing value untouched.
    updateMutation.mutate(
      {
        versionId: version.id,
        alias: trimmedAlias,
        notes: trimmedNotes,
      },
      { onSuccess: () => setEditingVersionId(null) }
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex w-[600px] flex-col sm:max-w-[600px]"
      >
        {/* Header */}
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-lg font-semibold">
              Manage Versions
            </SheetTitle>
            <SheetClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 cursor-pointer"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {isError && (
            <p className="text-sm text-destructive">
              Failed to load versions. Please try again.
            </p>
          )}

          {!isLoading && !isError && versions?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No versions found for this plan.
            </p>
          )}

          {!isLoading && !isError && versions && versions.length > 0 && (
            <div className="space-y-3">
              {versions.map(version => (
                <div
                  key={version.id}
                  className="rounded-lg border p-4 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {editingVersionId === version.id ? (
                        <div className="flex items-center gap-1.5">
                          <Input
                            value={editAliasInput}
                            onChange={e => setEditAliasInput(e.target.value)}
                            className="h-8 w-48"
                            autoFocus
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSaveEdit(version)
                              if (e.key === 'Escape') handleCancelEdit()
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 cursor-pointer"
                            onClick={() => handleSaveEdit(version)}
                            disabled={updateMutation.isPending}
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 cursor-pointer"
                            onClick={handleCancelEdit}
                            disabled={updateMutation.isPending}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 group/name">
                          <h3 className="text-sm font-semibold">
                            {version.alias}
                          </h3>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover/name:opacity-100 transition-opacity cursor-pointer"
                            onClick={() => handleStartEdit(version)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      {version.is_active && (
                        <Badge className="bg-[#CF1F2E] hover:bg-[#CF1F2E] text-white text-xs">
                          Active
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {!version.is_active &&
                        (status === 'DRAFT' || status === 'SENT_BACK') && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[#CF1F2E] hover:text-[#b81a27] cursor-pointer gap-1 h-8 text-xs"
                              disabled={activateMutation.isPending}
                              onClick={() =>
                                activateMutation.mutate(version.id)
                              }
                            >
                              {activateMutation.isPending &&
                              activateMutation.variables === version.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <RotateCcw className="h-3.5 w-3.5" />
                              )}
                              Set as Active
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive cursor-pointer"
                              disabled={deleteMutation.isPending}
                              onClick={() => setDeleteTargetId(version.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                    </div>
                  </div>
                  {editingVersionId === version.id ? (
                    <Textarea
                      value={editNotesInput}
                      onChange={e => setEditNotesInput(e.target.value)}
                      placeholder="Add notes..."
                      className="min-h-16 text-sm"
                      onKeyDown={e => {
                        if (e.key === 'Escape') handleCancelEdit()
                      }}
                    />
                  ) : (
                    version.notes && (
                      <p className="text-sm text-muted-foreground">
                        {version.notes}
                      </p>
                    )
                  )}
                  <p className="text-xs text-muted-foreground">
                    Created on {formatDate(version.created_at)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <SheetFooter className="border-t px-6 py-4">
          <SheetClose asChild>
            <Button variant="outline" className="w-full cursor-pointer">
              Close
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>

      <ConfirmationDialog
        open={deleteTargetId !== null}
        onOpenChange={isOpen => {
          if (!isOpen) setDeleteTargetId(null)
        }}
        title="Delete Version"
        description="Are you sure you want to delete this version? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        isLoading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTargetId) {
            deleteMutation.mutate(deleteTargetId, {
              onSuccess: () => setDeleteTargetId(null),
            })
          }
        }}
      />
    </Sheet>
  )
}
